import { useMemo } from "react";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import {
  GENIE_DOCUMENT_SOURCE_TYPE,
  readStoredGenieDocument,
  type StoredGenieDocument,
} from "@/types/genie-documents/genie-document";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

export interface GenieDocumentEntry {
  row: DocumentWithMetadata;
  stored: StoredGenieDocument;
  /** Published means it is live on this Genie's public page. */
  isPublished: boolean;
}

/**
 * Picks the branded documents out of a mixed document list, newest first.
 *
 * Exported so a page that has already loaded everything attached to a Genie can
 * split sources from documents without a second round trip.
 */
export const toGenieDocumentEntries = (
  rows: DocumentWithMetadata[],
): GenieDocumentEntry[] =>
  rows
    .map((row) => {
      const stored = readStoredGenieDocument(row);
      if (!stored) return null;
      return { row, stored, isPublished: row.is_public === true };
    })
    .filter((entry): entry is GenieDocumentEntry => entry !== null)
    .sort((a, b) => {
      const aTime = new Date(a.row.updated_at || a.row.created_at || 0).getTime();
      const bTime = new Date(b.row.updated_at || b.row.created_at || 0).getTime();
      return bTime - aTime;
    });

/**
 * Every branded document saved against one Genie.
 *
 * Reuses the ordinary document list filtered to our source type — branded
 * documents are documents, so there is nothing here that needs its own endpoint.
 */
export const useGenieDocuments = (genieId: string | undefined, enabled = true) => {
  const query = useDocuments(
    "my-documents",
    { agentId: genieId, selectedSourceType: GENIE_DOCUMENT_SOURCE_TYPE },
    undefined,
    Boolean(genieId) && enabled,
  );

  const documents = useMemo(() => toGenieDocumentEntries(query.data ?? []), [query.data]);

  return {
    documents,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
